import React, { useMemo } from "react";
import { useScheduleData, UseScheduleDataReturn } from "../scheduleData";
import { Checkbox } from "@/components/ui/checkbox";
import { getScheduleEventsByPharmacies } from "@/dispatchers/scheduleEventsDispatchers";

interface PharmaciesFilterProps {
  selectedPharmacies: string[];
  onPharmacyChange: (pharmacyId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  isDarkMode?: boolean;
}

export function PharmaciesFilter({
  selectedPharmacies,
  onPharmacyChange,
  onSelectAll,
  isDarkMode = false,
}: PharmaciesFilterProps) {
  const s = useScheduleData();
  const { pharmacies = [] } = (s || {}) as Partial<UseScheduleDataReturn>;
  const uniquePharmacies = useMemo(() => {
    const seen = new Map<string, any>();
    (pharmacies || []).forEach((p: any) => {
      const id = String(p.teamPharmacyID || p.id || "");
      if (!seen.has(id)) seen.set(id, p);
    });
    return Array.from(seen.values());
  }, [pharmacies]);
  const uniqueIds = useMemo(
    () =>
      uniquePharmacies.map((p: any) => String(p.teamPharmacyID || p.id || "")),
    [uniquePharmacies],
  );

  const fetchScheduleEventsForSelectedPharmacies = async (
    pharmacyIds: string[],
  ) => {
    const normalizedIds = Array.from(new Set(pharmacyIds.filter(Boolean)));
    try {
      const response = await getScheduleEventsByPharmacies(normalizedIds);
      return response;
    } catch (error) {
      console.error(
        "Error fetching schedule events for selected pharmacies:",
        error,
      );
      return null;
    }
  };

  return (
    <div
      className={`rounded-lg border p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"
      }`}
    >
      <h4
        className={`text-sm font-semibold mb-3 transition-colors duration-300 ${
          isDarkMode ? "text-white" : "text-gray-900"
        }`}
      >
        Pharmacies
      </h4>
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all-services"
            checked={
              uniqueIds.length > 0 &&
              uniqueIds.every((id) => (selectedPharmacies || []).includes(id))
            }
            onCheckedChange={async (checked) => {
              const isChecked = !!checked;
              const newArr = isChecked ? uniqueIds : [];
              onSelectAll(isChecked);
              await fetchScheduleEventsForSelectedPharmacies(newArr);
            }}
          />
          <label
            htmlFor="select-all-services"
            className={`text-sm cursor-pointer transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Select All
          </label>
        </div>
        <hr
          className={`transition-colors duration-300 ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        />
        {uniquePharmacies.map((pharmacy: any) => {
          const id = String(pharmacy.teamPharmacyID || pharmacy.id || "");
          const label = pharmacy.teamPharmacyName || "";

          return (
            <div key={id} className="flex items-center space-x-2">
              <Checkbox
                id={`pharmacy-${id}`}
                checked={(selectedPharmacies || []).includes(id)}
                onCheckedChange={async (checked) => {
                  const isChecked = !!checked;
                  const base = Array.isArray(selectedPharmacies)
                    ? selectedPharmacies
                    : [];
                  const newArr = isChecked
                    ? Array.from(new Set([...base, id]))
                    : base.filter((x) => x !== id);
                  onPharmacyChange(id, isChecked);
                  await fetchScheduleEventsForSelectedPharmacies(newArr);
                }}
              />
              <label
                htmlFor={`pharmacy-${id}`}
                className={`text-sm cursor-pointer transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
